import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, Grid, Paper, Switch, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import moment from "moment";
import TabHeader from "src/components/TabHeader/TabHeader";
import { useStyles } from "../Manifest-Entry/components/style";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import CommonButton from "src/components/CommonButton/CommonButton";
import { filteryByArr } from "src/common/utils/app.dropdowns";
import { filterBy } from "src/common/utils/helpers";

type PreArrivalNotificationRow = {
  containerNo?: string;
  virNo?: string;
  vessel?: string;
  arrivalDate?: string;
  portName?: string;
  vehicleNumber?: string;
  portOfEntry?: string;
  pccssSeal?: string;
  performed?: string;
  blNo?: string;
  indexNo?: number;
  manifestedSealNo?: string;
  shippingLine?: string;
  status?: string;
  isoCode?: string;
  hsCode?: string;
  blGrossWeight?: number;
  containerGrossWeight?: number;
  numberOfPackages?: number;
  packageType?: string;
  description?: string;
  markAndNumber?: string;
  destinationCode?: string;
  countryCode?: string;
  virType?: string;
  messageFrom?: string;
  messageTo?: string;
  imoClass?: string;
  opType?: string;
  isExecuted?: number;
  [key: string]: any;
};

const getTodayDateRange = () => filterBy({ value: "today" });

const cellValue = (value: any) =>
  value !== undefined && value !== null && String(value).trim() !== "" ? value : " ";

const extractImportManifestRows = (res: any): PreArrivalNotificationRow[] =>
  Array.isArray(res?.igmoRecords) ? res.igmoRecords : [];

const preArrivalColumns = [
  {
    accessorKey: "srNo",
    header: "Sr.No",
    size: 70,
    accessorFn: (_row: any, index: number) => index + 1,
  },
  { accessorKey: "containerNo", header: "Container No", size: 180 },
  { accessorKey: "virNo", header: "VIR No", size: 240 },
  { accessorKey: "vehicleNumber", header: "Vehicle No", size: 160 },
  { accessorKey: "portOfEntry", header: "Port Of Entry", size: 140 },
  { accessorKey: "pccssSeal", header: "PCCSS Seal", size: 140 },
  {
    accessorKey: "performed",
    header: "Performed",
    size: 200,
    accessorFn: (row: any) => cellValue(row?.performed),
  },
];

const importManifestColumns = [
  {
    accessorKey: "srNo",
    header: "Sr.No",
    size: 70,
    accessorFn: (_row: any, index: number) => index + 1,
  },
  {
    accessorKey: "containerNo",
    header: "Container No",
    size: 170,
    accessorFn: (row: any) => cellValue(row?.containerNo),
  },
  {
    accessorKey: "virNo",
    header: "VIR No",
    size: 220,
    accessorFn: (row: any) => cellValue(row?.virNo),
  },
  {
    accessorKey: "vessel",
    header: "Vessel",
    size: 160,
    accessorFn: (row: any) => cellValue(row?.vessel),
  },
  {
    accessorKey: "arrivalDate",
    header: "Arrival Date",
    size: 130,
    accessorFn: (row: any) =>
      row?.arrivalDate ? moment(row.arrivalDate).format("DD-MM-YYYY") : cellValue(""),
  },
  {
    accessorKey: "portName",
    header: "Port Name",
    size: 140,
    accessorFn: (row: any) => cellValue(row?.portName),
  },
  {
    accessorKey: "blNo",
    header: "BL No",
    size: 160,
    accessorFn: (row: any) => cellValue(row?.blNo),
  },
  {
    accessorKey: "indexNo",
    header: "Index No",
    size: 100,
    accessorFn: (row: any) => cellValue(row?.indexNo),
  },
  {
    accessorKey: "manifestedSealNo",
    header: "Manifested Seal No",
    size: 150,
    accessorFn: (row: any) => cellValue(row?.manifestedSealNo),
  },
  {
    accessorKey: "shippingLine",
    header: "Shipping Line",
    size: 130,
    accessorFn: (row: any) => cellValue(row?.shippingLine),
  },
  {
    accessorKey: "status",
    header: "Status",
    size: 90,
    accessorFn: (row: any) => cellValue(row?.status),
  },
  {
    accessorKey: "isoCode",
    header: "ISO Code",
    size: 100,
    accessorFn: (row: any) => cellValue(row?.isoCode),
  },
  // {
  //   accessorKey: "hsCode",
  //   header: "HS Code",
  //   size: 110,
  //   accessorFn: (row: any) => cellValue(row?.hsCode),
  // },
  {
    accessorKey: "blGrossWeight",
    header: "BL Gross Weight",
    size: 140,
    accessorFn: (row: any) => cellValue(row?.blGrossWeight),
  },
  {
    accessorKey: "containerGrossWeight",
    header: "Container Gross Weight",
    size: 170,
    accessorFn: (row: any) => cellValue(row?.containerGrossWeight),
  },
  // {
  //   accessorKey: "numberOfPackages",
  //   header: "No. of Packages",
  //   size: 130,
  //   accessorFn: (row: any) => cellValue(row?.numberOfPackages),
  // },
  // {
  //   accessorKey: "packageType",
  //   header: "Package Type",
  //   size: 160,
  //   accessorFn: (row: any) => cellValue(row?.packageType),
  // },
  {
    accessorKey: "description",
    header: "Description",
    size: 280,
    accessorFn: (row: any) => cellValue(row?.description),
  },
  {
    accessorKey: "markAndNumber",
    header: "Mark & Number",
    size: 220,
    accessorFn: (row: any) => cellValue(row?.markAndNumber),
  },
  // {
  //   accessorKey: "destinationCode",
  //   header: "Destination Code",
  //   size: 140,
  //   accessorFn: (row: any) => cellValue(row?.destinationCode),
  // },
  // {
  //   accessorKey: "countryCode",
  //   header: "Country Code",
  //   size: 120,
  //   accessorFn: (row: any) => cellValue(row?.countryCode),
  // },
  {
    accessorKey: "virType",
    header: "VIR Type",
    size: 110,
    accessorFn: (row: any) => cellValue(row?.virType),
  },
  {
    accessorKey: "performed",
    header: "Performed",
    size: 180,
    accessorFn: (row: any) => cellValue(row?.performed),
  },
  // {
  //   accessorKey: "messageFrom",
  //   header: "Message From",
  //   size: 120,
  //   accessorFn: (row: any) => cellValue(row?.messageFrom),
  // },
  // {
  //   accessorKey: "messageTo",
  //   header: "Message To",
  //   size: 110,
  //   accessorFn: (row: any) => cellValue(row?.messageTo),
  // },
  // {
  //   accessorKey: "imoClass",
  //   header: "IMO Class",
  //   size: 110,
  //   accessorFn: (row: any) => cellValue(row?.imoClass),
  // },
  // {
  //   accessorKey: "opType",
  //   header: "Op Type",
  //   size: 100,
  //   accessorFn: (row: any) => cellValue(row?.opType),
  // },
  // {
  //   accessorKey: "isExecuted",
  //   header: "Is Executed",
  //   size: 110,
  //   accessorFn: (row: any) => cellValue(row?.isExecuted),
  // },
];

export default function PreArrivalNotification(): React.ReactElement {
  const classes = useStyles();
  const moduleName = "Gate Entry";
  const pageHeading = "Arrival Notifications";
  const [rows, setRows] = useState<PreArrivalNotificationRow[]>([]);
  const [loader, setLoader] = useState(false);
  const [isPreArrival, setIsPreArrival] = useState(false);
  const [isCustomDate, setIsCustomDate] = useState(false);
  const startDatePickerRef = useRef<any>(null);
  const endDatePickerRef = useRef<any>(null);
  const todayRange = useMemo(() => getTodayDateRange(), []);

  const formik = useFormik({
    initialValues: {
      filterBy: "today",
      startDate: todayRange.startDate,
      endDate: todayRange.endDate,
    },
    onSubmit: async (values) => {
      await fetchImportManifest(values.startDate, values.endDate);
    },
  });

  const columns = useMemo(
    () => (isPreArrival ? preArrivalColumns : importManifestColumns),
    [isPreArrival]
  );

  const exportHandler = async (data: PreArrivalNotificationRow[]) => {
    if (isPreArrival) {
      return (data || []).map((r, index) => ({
        "Sr.No": index + 1,
        "Container No": r?.containerNo || "",
        "VIR No": r?.virNo || "",
        "Vehicle No": r?.vehicleNumber || "",
        "Port Of Entry": r?.portOfEntry || "",
        "PCCSS Seal": r?.pccssSeal || "",
        Performed: r?.performed || "",
      }));
    }

    return (data || []).map((r, index) => ({
      "Sr.No": index + 1,
      "Container No": r?.containerNo || "",
      "VIR No": r?.virNo || "",
      Vessel: r?.vessel || "",
      "Arrival Date": r?.arrivalDate ? moment(r.arrivalDate).format("DD-MM-YYYY") : "",
      "Port Name": r?.portName || "",
      "BL No": r?.blNo || "",
      "Index No": r?.indexNo ?? "",
      "Manifested Seal No": r?.manifestedSealNo || "",
      "Shipping Line": r?.shippingLine || "",
      Status: r?.status || "",
      "ISO Code": r?.isoCode || "",
      // "HS Code": r?.hsCode || "",
      "BL Gross Weight": r?.blGrossWeight ?? "",
      "Container Gross Weight": r?.containerGrossWeight ?? "",
      // "No. of Packages": r?.numberOfPackages ?? "",
      // "Package Type": r?.packageType || "",
      Description: r?.description || "",
      "Mark & Number": r?.markAndNumber || "",
      // "Destination Code": r?.destinationCode || "",
      // "Country Code": r?.countryCode || "",
      "VIR Type": r?.virType || "",
      Performed: r?.performed || "",
      // "Message From": r?.messageFrom || "",
      // "Message To": r?.messageTo || "",
      // "IMO Class": r?.imoClass || "",
      // "Op Type": r?.opType || "",
      // "Is Executed": r?.isExecuted ?? "",
    }));
  };

  const fetchImportManifest = async (startDate: string, endDate: string) => {
    setLoader(true);
    try {
      const query = `startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(
        endDate
      )}`;
      const res: any = await fetchWrapper.get(`/get-igmo-by-date-range?${query}`);
      setRows(extractImportManifestRows(res));
    } catch {
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  const fetchPreArrivalNotifications = async () => {
    setLoader(true);
    try {
      const res: any = await fetchWrapper.get("/get-pre-arrival-notification");
      setRows(Array.isArray(res?.preArrivalNotifications) ? res.preArrivalNotifications : []);
    } catch {
      setRows([]);
    } finally {
      setLoader(false);
    }
  };

  const filterByAction = (data: any) => {
    formik.setFieldValue("filterBy", data.value);
    if (data?.value) {
      const res = filterBy(data);
      setIsCustomDate(Boolean(res?.isCustomDate));
      formik.setFieldValue("startDate", res?.startDate);
      formik.setFieldValue("endDate", res?.endDate);
    }
  };

  useEffect(() => {
    setRows([]);
    if (isPreArrival) {
      fetchPreArrivalNotifications();
    } else {
      fetchImportManifest(formik.values.startDate, formik.values.endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreArrival]);

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} link={"/gate-entry"} />
      <Box m={2} />
      <Grid container direction="row">
        <Grid item xs={12} md={12} className={classes.cardMainParent}>
          <Paper elevation={3} className={classes.cardMain}>
            <Box pl={2} pr={2}>
              <Grid container direction="row">
                <Grid item xs={12} md={7}>
                  <Typography className={classes.pageHeading}>{pageHeading}</Typography>
                </Grid>
              </Grid>

              <Box className={classes.modeSwitchRow} mt={1}>
                <Typography className={classes.modeSwitchLabel}>Import Manifest</Typography>
                <Switch
                  checked={isPreArrival}
                  onChange={(e) => {
                    setRows([]);
                    setIsPreArrival(e.target.checked);
                  }}
                  color="primary"
                />
                <Typography className={classes.modeSwitchLabel}>Pre Arrival</Typography>
              </Box>

              {!isPreArrival && (
                <Grid spacing={2} container direction="row" className={classes.filterRow}>
                  <Grid item xs={12} md={3} lg={2}>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      label="Filter by"
                      placeholder="Filter by"
                      name="filterBy"
                      disableClearable={true}
                      optionsType={filteryByArr}
                      getOptionLabel={(option: any) => option.label || ""}
                      onChange={(row: any) => {
                        if (row) {
                          filterByAction(row);
                        }
                      }}
                      value={
                        filteryByArr.find(({ value }: any) => value === formik.values.filterBy) ||
                        ""
                      }
                    />
                  </Grid>
                  {isCustomDate && (
                    <>
                      <Grid item xs={12} md={3} lg={2}>
                        <Box className={classes.filterDateFieldWrapper}>
                          <CommonTextFieldDate
                            cssClassName={`${classes.filterDateField} customTextField`}
                            label="Start Date"
                            name="startDate"
                            datePickerRef={startDatePickerRef}
                            value={formik.values.startDate}
                            onChange={(e: any) => {
                              formik.setFieldValue(
                                "startDate",
                                moment(e).format("YYYY-MM-DDTHH:mm")
                              );
                              formik.setFieldValue("endDate", "");
                            }}
                            maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3} lg={2}>
                        <Box className={classes.filterDateFieldWrapper}>
                          <CommonTextFieldDate
                            cssClassName={`${classes.filterDateField} customTextField`}
                            label="End Date"
                            name="endDate"
                            datePickerRef={endDatePickerRef}
                            value={formik.values.endDate}
                            onChange={(e: any) => {
                              formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                            }}
                            minDate={formik.values.startDate}
                            maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                          />
                        </Box>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={3} lg={2}>
                    <CommonButton
                      cssClass="customSubmitBtn"
                      isGreen
                      disable={loader}
                      onClick={() => formik.submitForm()}
                    >
                      Search
                    </CommonButton>
                  </Grid>
                </Grid>
              )}
            </Box>
            <Box m={1}>
              <Divider />
            </Box>

            <Box p={2}>
              <CommonMaterialGrid
                key={isPreArrival ? "pre-arrival" : "import-manifest"}
                height={550}
                loader={loader}
                rows={rows || []}
                columns={columns}
                isExport
                filename={isPreArrival ? "pre-arrival-notifications" : "import-manifest-records"}
                exportHandler={exportHandler}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
}
